import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { 
  ArrowLeft, 
  ExternalLink, 
  Edit2, 
  Trash2, 
  Copy,
  Check,
  Loader2,
  User,
  Calendar,
  Clock,
  Link2,
  FileText,
  Building2,
  Save,
  X
} from 'lucide-react';

const DECK_STATUSES = [
  { id: 'concept', label: 'Concept', color: '#6B7280' },
  { id: 'presented', label: 'Presented', color: '#3B82F6' },
  { id: 'samples_requested', label: 'Samples Requested', color: '#F59E0B' },
  { id: 'approved', label: 'Approved', color: '#10B981' },
  { id: 'closed', label: 'Closed', color: '#1F2937' },
];

export default function DeckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { get, put, del, loading } = useApi();
  
  const [deck, setDeck] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editData, setEditData] = useState({
    title: '',
    deck_url: '',
    opportunity_id: '',
    status: '',
    internal_notes: '',
  });

  const fetchDeck = async () => {
    try {
      const data = await get(`/deck-library/${id}`);
      setDeck(data);
      setEditData({
        title: data.title,
        deck_url: data.deck_url,
        opportunity_id: data.opportunity_id || 'none',
        status: data.status,
        internal_notes: data.internal_notes || '',
      });
    } catch (error) {
      toast.error('Deck not found');
      navigate('/deck-library');
    }
  };

  const fetchOpportunities = async () => {
    try {
      const data = await get('/opportunities');
      setOpportunities(data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    }
  };

  useEffect(() => {
    fetchDeck();
    fetchOpportunities();
  }, [id]);

  const canEdit = () => {
    if (!deck) return false;
    if (isAdmin) return true;
    return deck.created_by === user?.id;
  };

  const canDelete = () => {
    if (!deck) return false;
    if (isAdmin) return true;
    return deck.created_by === user?.id;
  };

  const getStatusBadge = (statusId) => {
    const status = DECK_STATUSES.find(s => s.id === statusId);
    if (!status) return null;
    return (
      <Badge 
        className="rounded-sm text-xs font-medium"
        style={{ backgroundColor: status.color, color: '#fff' }}
      >
        {status.label}
      </Badge>
    );
  };

  const getAccountName = (oppId) => {
    if (!oppId || oppId === 'none') return 'Global';
    const opp = opportunities.find(o => o.id === oppId);
    return opp?.account_name || 'Global';
  };

  const getLinkedOpportunity = () => {
    if (!deck?.opportunity_id) return null;
    return opportunities.find(o => o.id === deck.opportunity_id);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(deck.deck_url);
      setCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const validateUrl = (url) => {
    if (!url.startsWith('https://')) {
      toast.error('URL must start with https://');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateUrl(editData.deck_url)) return;
    
    setSaving(true);
    try {
      const submitData = {
        ...editData,
        opportunity_id: editData.opportunity_id === 'none' ? '' : editData.opportunity_id
      };
      await put(`/deck-library/${id}`, submitData);
      toast.success('Deck updated successfully');
      setIsEditing(false);
      fetchDeck();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/deck-library/${id}`);
      toast.success('Deck deleted successfully');
      navigate('/deck-library');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!canEdit()) return;
    
    try {
      await put(`/deck-library/${id}`, { status: newStatus });
      setDeck(prev => ({ ...prev, status: newStatus }));
      toast.success('Status updated');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!deck) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const linkedOpp = getLinkedOpportunity();

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="deck-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/deck-library')}
          className="rounded-sm -ml-2"
          data-testid="back-to-library"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Deck Library
        </Button>

        {/* Header Card */}
        <Card className="rounded-sm border">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                {isEditing ? (
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                    className="text-2xl font-semibold mb-2 rounded-sm"
                    data-testid="edit-title"
                  />
                ) : (
                  <h1 className="text-2xl font-semibold mb-2">{deck.title}</h1>
                )}
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-4 w-4" />
                    <span>{getAccountName(deck.opportunity_id)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    <span>{deck.created_by_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    <span>Created {formatDateTime(deck.created_at).split(',')[0]}</span>
                  </div>
                  {deck.updated_at !== deck.created_at && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4" />
                      <span>Updated {formatDateTime(deck.updated_at).split(',')[0]}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Status Dropdown */}
                {canEdit() ? (
                  <Select value={deck.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-[160px] rounded-sm" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DECK_STATUSES.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: status.color }}
                            />
                            {status.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  getStatusBadge(deck.status)
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Primary Action - Open Deck */}
        <Card className="rounded-sm border bg-primary/5">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-medium mb-1">Deck Presentation</h2>
                <p className="text-sm text-muted-foreground">
                  Open the hosted deck in a new tab
                </p>
              </div>
              <Button 
                size="lg"
                onClick={() => window.open(deck.deck_url, '_blank')}
                className="rounded-sm"
                data-testid="open-deck-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Deck
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="rounded-sm border">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Deck URL */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Deck URL
              </Label>
              {isEditing ? (
                <Input
                  value={editData.deck_url}
                  onChange={(e) => setEditData(prev => ({ ...prev, deck_url: e.target.value }))}
                  placeholder="https://..."
                  className="rounded-sm"
                  data-testid="edit-url"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded-sm text-sm truncate">
                    {deck.deck_url}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="rounded-sm shrink-0"
                    data-testid="copy-url-btn"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Internal Notes</Label>
              {isEditing ? (
                <Textarea
                  value={editData.internal_notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, internal_notes: e.target.value }))}
                  placeholder="Add notes for internal reference..."
                  rows={3}
                  className="rounded-sm resize-none"
                  data-testid="edit-notes"
                />
              ) : (
                <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-sm min-h-[80px]">
                  {deck.internal_notes || 'No notes added'}
                </p>
              )}
            </div>

            {/* Linked Opportunity */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Linked Opportunity</Label>
              {isEditing ? (
                <Select
                  value={editData.opportunity_id}
                  onValueChange={(value) => setEditData(prev => ({ ...prev, opportunity_id: value }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="edit-opportunity">
                    <SelectValue placeholder="Select opportunity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked opportunity</SelectItem>
                    {opportunities.map((opp) => (
                      <SelectItem key={opp.id} value={opp.id}>
                        {opp.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : linkedOpp ? (
                <Link 
                  to={`/opportunity/${linkedOpp.id}`}
                  className="flex items-center gap-2 p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                >
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{linkedOpp.account_name}</span>
                  <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground" />
                </Link>
              ) : (
                <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-sm">
                  No linked opportunity
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => navigate('/deck-library')}
            className="rounded-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Library
          </Button>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setEditData({
                      title: deck.title,
                      deck_url: deck.deck_url,
                      opportunity_id: deck.opportunity_id || 'none',
                      status: deck.status,
                      internal_notes: deck.internal_notes || '',
                    });
                  }}
                  className="rounded-sm"
                  data-testid="cancel-edit-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-sm"
                  data-testid="save-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {canEdit() && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="rounded-sm"
                    data-testid="edit-btn"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Deck
                  </Button>
                )}
                {canDelete() && (
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="rounded-sm text-destructive hover:text-destructive"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Deck?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{deck.title}" from the library. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
